import { ReactNode } from "react";
import { BreadAccount } from "breadcommon";
import { InstitutionLogo } from "./InstitutionLogo";
import { useInstitutionFromAccount } from "../derived/useInstitutionFromAccount";
import { formatAccountSubtype } from "../derived/accountBalances";
import { buildClasses } from "../utils/buildClasses";

/** Logo size wherever an account is listed with its details line. */
export const ACCOUNT_LOGO_SIZE = 32;

/**
 * An account's logo, name and a details line (bank · type · last 4 digits),
 * as listed on the Accounts and Balances screens. Pass `name` to replace the
 * plain name, e.g. with an editable one.
 */
export function AccountIdentity({
  account,
  name,
}: {
  account: BreadAccount;
  name?: ReactNode;
}): JSX.Element {
  const institution = useInstitutionFromAccount(account);
  const details = [
    institution?.name,
    account.subtype ? formatAccountSubtype(account.subtype) : null,
    account.mask ? `•••• ${account.mask}` : null,
  ].filter((part): part is string => !!part);

  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "gap-3",
        "flex-1",
        "min-w-0",
      )}
    >
      <InstitutionLogo
        institution={institution}
        size={ACCOUNT_LOGO_SIZE}
        className="flex-shrink-0"
      />
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "gap-0.5",
          "flex-1",
          "min-w-0",
        )}
      >
        {name ?? (
          <span className={buildClasses("truncate")}>{account.name}</span>
        )}
        {details.length > 0 && (
          <span
            className={buildClasses(
              "text-sm",
              "text-on-surface-500",
              "truncate",
            )}
          >
            {details.join(" · ")}
          </span>
        )}
      </div>
    </div>
  );
}
